import React, { useState, useRef, useEffect, useCallback } from 'react';
import { WordItem } from '../types';
import { saveWordOverride } from '../utils/wordOverridesStorage';
import { ALL_CHALLENGE_CATEGORIES, getCategoryIcon } from '../utils/challengeWords';
import { getWordIcon, getWordIconSuggestions } from '../utils/emojis';
import { classifyWordCategory } from '../utils/wordClassifier';
import { playSound, triggerHaptic } from '../audio';
import { X, Sparkles, Check, Keyboard } from 'lucide-react';

interface AddPublicWordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWordAdded?: (word: WordItem) => void;
  existingCategories?: string[];
}

// Unified Tamazight Keyboard Rows (Ergonomic AZERTY + Dedicated Amazigh Ribbon)
const KEYBOARD_ROW_1 = ['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'];
const KEYBOARD_ROW_2 = ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'];
const KEYBOARD_ROW_3 = ['W', 'X', 'C', 'V', 'B', 'N', "'", '-'];
const KEYBOARD_AMAZIGH_ROW = ['Ɛ', 'Ɣ', 'Ḥ', 'Ṣ', 'Ṭ', 'Ḍ', 'Č', 'Ǧ', 'Ž', 'Ṛ', 'Ẓ'];

export const AddPublicWordModal: React.FC<AddPublicWordModalProps> = ({
  isOpen,
  onClose,
  onWordAdded,
  existingCategories = [],
}) => {
  const [kabText, setKabText] = useState('');
  const [frText, setFrText] = useState('');
  const [pluralKabText, setPluralKabText] = useState('');
  const [singularKabText, setSingularKabText] = useState('');
  const [variantsText, setVariantsText] = useState('');
  const [gender, setGender] = useState<'m' | 'f' | undefined>(undefined);
  const [grammaticalNumber, setGrammaticalNumber] = useState<'sg' | 'pl' | undefined>(undefined);
  const [category, setCategory] = useState('Général');
  const [iconText, setIconText] = useState('');
  const [conjugationAccompliText, setConjugationAccompliText] = useState('');
  const [conjugationInaccompliText, setConjugationInaccompliText] = useState('');
  const [activeInput, setActiveInput] = useState<'kab' | 'fr' | 'pluralKab' | 'singularKab' | 'variants' | 'icon' | 'conjugationAccompli' | 'conjugationInaccompli'>('kab');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCapitalized, setIsCapitalized] = useState<boolean>(false);
  const [useVirtualKeyboard, setUseVirtualKeyboard] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Transient illuminated key state for virtual keyboard pop feedback
  const [illuminatedKey, setIlluminatedKey] = useState<string | null>(null);
  const illuminateTimerRef = useRef<NodeJS.Timeout | null>(null);

  const triggerKeyFeedback = useCallback((key: string) => {
    triggerHaptic('light');
    setIlluminatedKey(key);
    if (illuminateTimerRef.current) clearTimeout(illuminateTimerRef.current);
    illuminateTimerRef.current = setTimeout(() => {
      setIlluminatedKey(null);
    }, 160);
  }, []);

  const kabInputRef = useRef<HTMLInputElement>(null);
  const frInputRef = useRef<HTMLInputElement>(null);
  const pluralKabInputRef = useRef<HTMLInputElement>(null);
  const singularKabInputRef = useRef<HTMLInputElement>(null);
  const variantsInputRef = useRef<HTMLInputElement>(null);
  const conjugationAccompliInputRef = useRef<HTMLInputElement>(null);
  const conjugationInaccompliInputRef = useRef<HTMLInputElement>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);

  // Focus Kabyle input on open
  useEffect(() => {
    if (isOpen) {
      setKabText('');
      setFrText('');
      setPluralKabText('');
      setSingularKabText('');
      setVariantsText('');
      setConjugationAccompliText('');
      setConjugationInaccompliText('');
      setGender(undefined);
      setGrammaticalNumber(undefined);
      setCategory('Général');
      setIconText('');
      setErrorMessage(null);
      setIsCapitalized(false);
      setUseVirtualKeyboard(false);
      setTimeout(() => kabInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Handle virtual keyboard letter insertion
  const handleVirtualKey = useCallback((char: string) => {
    playSound('step');
    setErrorMessage(null);
    const charToInsert = isCapitalized ? char.toUpperCase() : char.toLowerCase();
    if (activeInput === 'kab') {
      setKabText((prev) => prev + charToInsert);
      kabInputRef.current?.focus();
    } else if (activeInput === 'fr') {
      setFrText((prev) => prev + charToInsert);
      frInputRef.current?.focus();
    } else if (activeInput === 'pluralKab') {
      setPluralKabText((prev) => prev + charToInsert);
      pluralKabInputRef.current?.focus();
    } else if (activeInput === 'singularKab') {
      setSingularKabText((prev) => prev + charToInsert);
      singularKabInputRef.current?.focus();
    } else if (activeInput === 'variants') {
      setVariantsText((prev) => prev + charToInsert);
      variantsInputRef.current?.focus();
    } else if (activeInput === 'conjugationAccompli') {
      setConjugationAccompliText((prev) => prev + charToInsert);
      conjugationAccompliInputRef.current?.focus();
    } else if (activeInput === 'conjugationInaccompli') {
      setConjugationInaccompliText((prev) => prev + charToInsert);
      conjugationInaccompliInputRef.current?.focus();
    }
  }, [activeInput, isCapitalized]);

  const handleBackspace = useCallback(() => {
    playSound('click');
    if (activeInput === 'kab') {
      setKabText((prev) => prev.slice(0, -1));
      kabInputRef.current?.focus();
    } else if (activeInput === 'fr') {
      setFrText((prev) => prev.slice(0, -1));
      frInputRef.current?.focus();
    } else if (activeInput === 'pluralKab') {
      setPluralKabText((prev) => prev.slice(0, -1));
      pluralKabInputRef.current?.focus();
    } else if (activeInput === 'singularKab') {
      setSingularKabText((prev) => prev.slice(0, -1));
      singularKabInputRef.current?.focus();
    } else if (activeInput === 'variants') {
      setVariantsText((prev) => prev.slice(0, -1));
      variantsInputRef.current?.focus();
    } else if (activeInput === 'conjugationAccompli') {
      setConjugationAccompliText((prev) => prev.slice(0, -1));
      conjugationAccompliInputRef.current?.focus();
    } else if (activeInput === 'conjugationInaccompli') {
      setConjugationInaccompliText((prev) => prev.slice(0, -1));
      conjugationInaccompliInputRef.current?.focus();
    }
  }, [activeInput]);

  const handleSpace = useCallback(() => {
    playSound('click');
    if (activeInput === 'kab') {
      setKabText((prev) => prev + ' ');
      kabInputRef.current?.focus();
    } else if (activeInput === 'fr') {
      setFrText((prev) => prev + ' ');
      frInputRef.current?.focus();
    } else if (activeInput === 'pluralKab') {
      setPluralKabText((prev) => prev + ' ');
      pluralKabInputRef.current?.focus();
    } else if (activeInput === 'singularKab') {
      setSingularKabText((prev) => prev + ' ');
      singularKabInputRef.current?.focus();
    } else if (activeInput === 'variants') {
      setVariantsText((prev) => prev + ' ');
      variantsInputRef.current?.focus();
    } else if (activeInput === 'conjugationAccompli') {
      setConjugationAccompliText((prev) => prev + ' ');
      conjugationAccompliInputRef.current?.focus();
    } else if (activeInput === 'conjugationInaccompli') {
      setConjugationInaccompliText((prev) => prev + ' ');
      conjugationInaccompliInputRef.current?.focus();
    }
  }, [activeInput]);

  const handleClear = useCallback(() => {
    playSound('click');
    if (activeInput === 'kab') {
      setKabText('');
      kabInputRef.current?.focus();
    } else if (activeInput === 'fr') {
      setFrText('');
      frInputRef.current?.focus();
    } else if (activeInput === 'pluralKab') {
      setPluralKabText('');
      pluralKabInputRef.current?.focus();
    } else if (activeInput === 'singularKab') {
      setSingularKabText('');
      singularKabInputRef.current?.focus();
    } else if (activeInput === 'variants') {
      setVariantsText('');
      variantsInputRef.current?.focus();
    }
  }, [activeInput]);

  if (!isOpen) return null;

  const handleSave = async () => {
    const finalKab = kabText.trim();
    const finalFr = frText.trim();
    const finalPluralKab = pluralKabText.trim() || undefined;
    const finalSingularKab = singularKabText.trim() || undefined;
    let resolvedNumber = grammaticalNumber;
    if (!resolvedNumber) {
      if (finalSingularKab) resolvedNumber = 'pl';
      else if (finalPluralKab) resolvedNumber = 'sg';
    }

    if (!finalKab) {
      setErrorMessage('Veuillez saisir le mot en kabyle.');
      playSound('wrong');
      kabInputRef.current?.focus();
      return;
    }

    if (!finalFr) {
      setErrorMessage('Veuillez saisir la traduction en français.');
      playSound('wrong');
      frInputRef.current?.focus();
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);

    try {
      let cleanCategory = category.trim() || 'Général';
      if (cleanCategory.toLowerCase() === 'général' || cleanCategory.toLowerCase() === 'general') {
        cleanCategory = classifyWordCategory({ kab: finalKab, fr: finalFr, type: cleanCategory });
      }
      const cleanIcon = iconText.trim() || getWordIcon({ kab: finalKab, fr: finalFr, type: cleanCategory } as WordItem) || getCategoryIcon(cleanCategory) || '📝';

      const finalVariants = variantsText.trim() || undefined;
      const finalConjugationAccompli = conjugationAccompliText.trim() || undefined;
      const finalConjugationInaccompli = conjugationInaccompliText.trim() || undefined;

      let finalPluralKab: string = '';
      let finalSingularKab: string = '';
      let resolvedNumber: 'sg' | 'pl' | undefined = grammaticalNumber;

      if (grammaticalNumber === 'sg') {
        finalPluralKab = pluralKabText.trim();
        finalSingularKab = '';
      } else if (grammaticalNumber === 'pl') {
        finalSingularKab = singularKabText.trim();
        finalPluralKab = '';
      } else {
        finalPluralKab = '';
        finalSingularKab = '';
        resolvedNumber = undefined;
      }

      await saveWordOverride(
        finalKab,
        finalKab,
        finalFr,
        cleanIcon,
        cleanCategory,
        true,
        gender,
        finalPluralKab,
        finalSingularKab,
        resolvedNumber,
        finalVariants,
        finalConjugationAccompli,
        finalConjugationInaccompli
      );

      playSound('victory');

      const newWord: WordItem = {
        kab: finalKab,
        fr: finalFr,
        icon: cleanIcon,
        type: cleanCategory,
        gender: gender,
        grammaticalNumber: resolvedNumber,
        pluralKab: finalPluralKab || undefined,
        singularKab: finalSingularKab || undefined,
        variants: finalVariants,
        conjugationAccompli: finalConjugationAccompli,
        conjugationInaccompli: finalConjugationInaccompli,
        originalKab: finalKab,
        originalFr: finalFr,
        originalIcon: cleanIcon,
      };

      if (onWordAdded) {
        onWordAdded(newWord);
      }

      onClose();
    } catch (err: any) {
      setErrorMessage('Erreur lors de la sauvegarde du mot sur le serveur.');
      playSound('wrong');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-xs flex flex-col overflow-hidden font-mono select-none w-full h-[100dvh]">
      {/* Header */}
      <div className="bg-[#ede4d4] px-4 py-3 border-b border-amber-900/15 flex items-center justify-between shrink-0 shadow-xs z-10">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-amber-600 to-amber-500 flex items-center justify-center text-white font-black shadow-xs">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-base font-black text-amber-950 uppercase tracking-wider leading-tight">
              Ajouter un mot public
            </h3>
            <p className="text-[10px] text-stone-600 font-medium">
              Formulaire personnalisé avec clavier berbère
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            playSound('click');
            onClose();
          }}
          className="w-8 h-8 rounded-lg bg-white/80 hover:bg-white text-stone-600 hover:text-stone-900 flex items-center justify-center border border-amber-900/20 cursor-pointer transition-all shadow-2xs"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Form Body - Takes top half */}
      <div className="flex-1 overflow-y-auto p-4 bg-[#fbf7ee] text-stone-900">
        <div className="w-full max-w-xl mx-auto space-y-4 pb-4">
          {errorMessage && (
            <div className="p-2.5 bg-rose-50 border border-rose-300 rounded-xl text-rose-800 text-xs font-bold flex items-center gap-2 shadow-2xs">
              <span>⚠️</span>
              <span>{errorMessage}</span>
            </div>
          )}

          {/* 1. Mot en Kabyle (Obligatoire) */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-amber-950 uppercase tracking-wider flex items-center gap-1.5">
              <span>Mot en Kabyle (Tamazight)</span>
              <span className="text-red-600 text-sm">*</span>
            </label>
            <div className="relative">
              <input
                ref={kabInputRef}
                type="text"
                inputMode={useVirtualKeyboard ? "none" : "text"}
                value={kabText}
                onFocus={() => setActiveInput('kab')}
                onChange={(e) => {
                  setKabText(e.target.value);
                  setErrorMessage(null);
                }}
                placeholder="Ex: Azul, Tilelli, Taddart..."
                className={`w-full bg-white border-2 rounded-xl pl-4 pr-11 py-2 text-base font-bold text-stone-900 focus:outline-none transition-all placeholder:text-stone-400 shadow-2xs ${
                  activeInput === 'kab'
                    ? 'border-amber-600 shadow-[0_0_10px_rgba(217,119,6,0.2)]'
                    : 'border-[#ded4c3]'
                }`}
              />
              {kabText && (
                <button
                  type="button"
                  onClick={() => {
                    setKabText('');
                    kabInputRef.current?.focus();
                  }}
                  className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-700 text-xs bg-stone-100 hover:bg-stone-200 px-1.5 py-0.5 rounded cursor-pointer"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* 2. Traduction */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-stone-800 uppercase tracking-wider flex items-center gap-1.5">
              <span>Traduction</span>
              <span className="text-red-600 text-sm">*</span>
            </label>
            <div className="relative">
              <input
                ref={frInputRef}
                type="text"
                inputMode={useVirtualKeyboard ? "none" : "text"}
                value={frText}
                onFocus={() => setActiveInput('fr')}
                onChange={(e) => {
                  setFrText(e.target.value);
                  setErrorMessage(null);
                }}
                placeholder="Ex: Bonjour, Liberté, Village..."
                className={`w-full bg-white border-2 rounded-xl pl-4 pr-11 py-2 text-base text-stone-900 font-semibold italic focus:outline-none transition-all placeholder:text-stone-400 shadow-2xs ${
                  activeInput === 'fr'
                    ? 'border-emerald-600 shadow-[0_0_10px_rgba(5,150,105,0.2)]'
                    : 'border-[#ded4c3]'
                }`}
              />
              {frText && (
                <button
                  type="button"
                  onClick={() => {
                    setFrText('');
                    frInputRef.current?.focus();
                  }}
                  className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-700 text-xs bg-stone-100 hover:bg-stone-200 px-1.5 py-0.5 rounded cursor-pointer"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* 2ter. Genre (Masculin / Féminin) */}
          <div className="space-y-1">
             <label className="text-xs font-bold text-stone-800 uppercase tracking-wider flex items-center justify-between">
              <span>Genre</span>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setGender(gender === 'm' ? undefined : 'm');
                }}
                className={`flex-1 py-1.5 rounded-xl border-2 text-xs font-bold transition-all cursor-pointer shadow-2xs ${
                  gender === 'm' ? 'bg-sky-50 border-sky-500 text-sky-900 shadow-xs' : 'bg-white border-[#ded4c3] text-stone-600 hover:border-stone-400 hover:text-stone-900'
                }`}
              >
                ♂️ Masculin
              </button>
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setGender(gender === 'f' ? undefined : 'f');
                }}
                className={`flex-1 py-1.5 rounded-xl border-2 text-xs font-bold transition-all cursor-pointer shadow-2xs ${
                  gender === 'f' ? 'bg-rose-50 border-rose-500 text-rose-900 shadow-xs' : 'bg-white border-[#ded4c3] text-stone-600 hover:border-stone-400 hover:text-stone-900'
                }`}
              >
                ♀️ Féminin
              </button>
            </div>
          </div>

          {/* 2quater. Sélecteur Singulier / Pluriel (Forme du mot principal) */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-stone-800 uppercase tracking-wider flex items-center justify-between">
              <span>Nombre</span>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  if (grammaticalNumber === 'sg') {
                    setGrammaticalNumber(undefined);
                  } else {
                    setGrammaticalNumber('sg');
                    setActiveInput('pluralKab');
                    setTimeout(() => pluralKabInputRef.current?.focus(), 50);
                  }
                }}
                className={`flex-1 py-1.5 rounded-xl border-2 text-xs font-bold transition-all cursor-pointer shadow-2xs ${
                  grammaticalNumber === 'sg'
                    ? 'bg-amber-100/90 border-amber-600 text-amber-950 shadow-xs'
                    : 'bg-white border-[#ded4c3] text-stone-600 hover:border-stone-400 hover:text-stone-900'
                }`}
              >
                📦 Singulier
              </button>
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  if (grammaticalNumber === 'pl') {
                    setGrammaticalNumber(undefined);
                  } else {
                    setGrammaticalNumber('pl');
                    setActiveInput('singularKab');
                    setTimeout(() => singularKabInputRef.current?.focus(), 50);
                  }
                }}
                className={`flex-1 py-1.5 rounded-xl border-2 text-xs font-bold transition-all cursor-pointer shadow-2xs ${
                  grammaticalNumber === 'pl'
                    ? 'bg-amber-100/90 border-amber-600 text-amber-950 shadow-xs'
                    : 'bg-white border-[#ded4c3] text-stone-600 hover:border-stone-400 hover:text-stone-900'
                }`}
              >
                📚 Pluriel
              </button>
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setGrammaticalNumber(undefined);
                  setPluralKabText('');
                  setSingularKabText('');
                }}
                className={`flex-1 py-1.5 rounded-xl border-2 text-xs font-bold transition-all cursor-pointer shadow-2xs ${
                  grammaticalNumber === undefined
                    ? 'bg-amber-100/90 border-amber-600 text-amber-950 shadow-xs'
                    : 'bg-white border-[#ded4c3] text-stone-600 hover:border-stone-400 hover:text-stone-900'
                }`}
                title="Pas de pluriel ou singulier (mot invariable)"
              >
                ➖ Invariable
              </button>
            </div>
          </div>

          {/* Forme opposée conditionnelle : Si Singulier sélectionné -> Champ Pluriel */}
          {grammaticalNumber === 'sg' && (
            <div className="space-y-1 p-2.5 bg-amber-50/80 border border-amber-300 rounded-xl animate-fadeIn shadow-2xs">
              <label className="text-xs font-bold text-amber-950 uppercase tracking-wider flex items-center justify-between">
                <span>Pluriel</span>
                <span className="text-[10px] text-amber-800 font-normal">Pluriel kabyle</span>
              </label>
              <div className="relative">
                <input
                  ref={pluralKabInputRef}
                  type="text"
                  inputMode={useVirtualKeyboard ? "none" : "text"}
                  value={pluralKabText}
                  onFocus={() => setActiveInput('pluralKab')}
                  onChange={(e) => {
                    setPluralKabText(e.target.value);
                    setErrorMessage(null);
                  }}
                  placeholder="Ex: Imezdaɣ, Tiwura..."
                  className={`w-full bg-white border-2 rounded-xl pl-4 pr-11 py-2 text-base text-stone-900 font-semibold focus:outline-none transition-all placeholder:text-stone-400 shadow-2xs ${
                    activeInput === 'pluralKab'
                      ? 'border-amber-600 shadow-[0_0_10px_rgba(217,119,6,0.2)]'
                      : 'border-[#ded4c3]'
                  }`}
                />
                {pluralKabText && (
                  <button
                    type="button"
                    onClick={() => {
                      setPluralKabText('');
                      pluralKabInputRef.current?.focus();
                    }}
                    className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-700 text-xs bg-stone-100 hover:bg-stone-200 px-1.5 py-0.5 rounded cursor-pointer"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Forme opposée conditionnelle : Si Pluriel sélectionné -> Champ Singulier */}
          {grammaticalNumber === 'pl' && (
            <div className="space-y-1 p-2.5 bg-amber-50/80 border border-amber-300 rounded-xl animate-fadeIn shadow-2xs">
              <label className="text-xs font-bold text-amber-950 uppercase tracking-wider flex items-center justify-between">
                <span>Singulier</span>
                <span className="text-[10px] text-amber-800 font-normal">Singulier kabyle</span>
              </label>
              <div className="relative">
                <input
                  ref={singularKabInputRef}
                  type="text"
                  inputMode={useVirtualKeyboard ? "none" : "text"}
                  value={singularKabText}
                  onFocus={() => setActiveInput('singularKab')}
                  onChange={(e) => {
                    setSingularKabText(e.target.value);
                    setErrorMessage(null);
                  }}
                  placeholder="Ex: Amezdaɣ, Tawwurt..."
                  className={`w-full bg-white border-2 rounded-xl pl-4 pr-11 py-2 text-base text-stone-900 font-semibold focus:outline-none transition-all placeholder:text-stone-400 shadow-2xs ${
                    activeInput === 'singularKab'
                      ? 'border-amber-600 shadow-[0_0_10px_rgba(217,119,6,0.2)]'
                      : 'border-[#ded4c3]'
                  }`}
                />
                {singularKabText && (
                  <button
                    type="button"
                    onClick={() => {
                      setSingularKabText('');
                      singularKabInputRef.current?.focus();
                    }}
                    className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-700 text-xs bg-stone-100 hover:bg-stone-200 px-1.5 py-0.5 rounded cursor-pointer"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          )}

          {/* 2quinquies. Variantes de mots */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-stone-800 uppercase tracking-wider flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <span>Variantes du mot</span>
                <span className="text-[10px] text-stone-500 font-normal lowercase">(optionnel)</span>
              </span>
            </label>
            <div className="relative">
              <input
                ref={variantsInputRef}
                type="text"
                inputMode={useVirtualKeyboard ? "none" : "text"}
                value={variantsText}
                onFocus={() => setActiveInput('variants')}
                onChange={(e) => {
                  setVariantsText(e.target.value);
                  setErrorMessage(null);
                }}
                placeholder="Ex: kraḍ, rebɛa, tamaziɣt..."
                className={`w-full bg-white border-2 rounded-xl pl-4 pr-11 py-2 text-base text-stone-900 font-medium focus:outline-none transition-all placeholder:text-stone-400 shadow-2xs ${
                  activeInput === 'variants'
                    ? 'border-amber-600 shadow-[0_0_10px_rgba(217,119,6,0.2)]'
                    : 'border-[#ded4c3]'
                }`}
              />
              {variantsText && (
                <button
                  type="button"
                  onClick={() => {
                    setVariantsText('');
                    variantsInputRef.current?.focus();
                  }}
                  className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-700 text-xs bg-stone-100 hover:bg-stone-200 px-1.5 py-0.5 rounded cursor-pointer"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* 2sexies. Conjugaison (Inaccompli & Accompli) - Visible uniquement pour la catégorie Verbes */}
          {((category || '').trim() === 'Verbes' || (category || '').trim() === "Verbes d'Action" || (category || '').toLowerCase().includes('verbe')) && (
            <div className="p-3 bg-amber-50/60 border border-amber-300/80 rounded-xl space-y-3 shadow-2xs animate-fadeIn">
              <div className="flex items-center justify-between pb-1.5 border-b border-amber-200/80">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-bold text-amber-950 uppercase tracking-wider">Conjugaison</span>
                  <span className="text-[11px] text-amber-800 font-normal">(verbes uniquement)</span>
                </div>
                <span className="text-[10px] text-amber-900 font-medium bg-amber-100/80 px-2 py-0.5 rounded border border-amber-300">
                  1re pers | 3e pers
                </span>
              </div>

              {/* Inaccompli (Présent / Habituel) */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-stone-700 flex items-center justify-between">
                  <span>Inaccompli (Présent / Habituel)</span>
                </label>
                <div className="relative">
                  <input
                    ref={conjugationInaccompliInputRef}
                    type="text"
                    inputMode={useVirtualKeyboard ? "none" : "text"}
                    value={conjugationInaccompliText}
                    onFocus={() => setActiveInput('conjugationInaccompli')}
                    onChange={(e) => {
                      setConjugationInaccompliText(e.target.value);
                      setErrorMessage(null);
                    }}
                    placeholder="Ex: Tteččeɣ (Je mange) | Itečč (Il mange)"
                    className={`w-full bg-white border-2 rounded-xl pl-4 pr-11 py-2 text-sm text-stone-900 font-medium focus:outline-none transition-all placeholder:text-stone-400 shadow-2xs ${
                      activeInput === 'conjugationInaccompli'
                        ? 'border-amber-600 shadow-[0_0_10px_rgba(217,119,6,0.2)]'
                        : 'border-stone-200'
                    }`}
                  />
                  {conjugationInaccompliText && (
                    <button
                      type="button"
                      onClick={() => {
                        setConjugationInaccompliText('');
                        conjugationInaccompliInputRef.current?.focus();
                      }}
                      className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-700 text-xs bg-stone-100 hover:bg-stone-200 px-1.5 py-0.5 rounded cursor-pointer"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Accompli (Passé) */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-stone-700 flex items-center justify-between">
                  <span>Accompli (Passé)</span>
                </label>
                <div className="relative">
                  <input
                    ref={conjugationAccompliInputRef}
                    type="text"
                    inputMode={useVirtualKeyboard ? "none" : "text"}
                    value={conjugationAccompliText}
                    onFocus={() => setActiveInput('conjugationAccompli')}
                    onChange={(e) => {
                      setConjugationAccompliText(e.target.value);
                      setErrorMessage(null);
                    }}
                    placeholder="Ex: Ččiɣ (J'ai mangé) | Yečča (Il a mangé)"
                    className={`w-full bg-white border-2 rounded-xl pl-4 pr-11 py-2 text-sm text-stone-900 font-medium focus:outline-none transition-all placeholder:text-stone-400 shadow-2xs ${
                      activeInput === 'conjugationAccompli'
                        ? 'border-amber-600 shadow-[0_0_10px_rgba(217,119,6,0.2)]'
                        : 'border-stone-200'
                    }`}
                  />
                  {conjugationAccompliText && (
                    <button
                      type="button"
                      onClick={() => {
                        setConjugationAccompliText('');
                        conjugationAccompliInputRef.current?.focus();
                      }}
                      className="absolute right-3 top-2.5 text-stone-400 hover:text-stone-700 text-xs bg-stone-100 hover:bg-stone-200 px-1.5 py-0.5 rounded cursor-pointer"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 3. Catégorie / Thématique du Défi */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-stone-800 uppercase tracking-wider flex items-center justify-between">
              <span>Thématique du Défi</span>
              <span className="text-[10px] text-stone-500 font-normal">Intègre le mot au dictionnaire public</span>
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-white border-2 border-[#ded4c3] rounded-xl px-3 py-2 text-xs sm:text-sm text-stone-900 focus:outline-none focus:border-amber-600 transition-all font-mono cursor-pointer shadow-2xs"
            >
              {ALL_CHALLENGE_CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
              {existingCategories
                .filter((ec) => !ALL_CHALLENGE_CATEGORIES.some((ac) => ac.id === ec || ac.name === ec))
                .map((cat) => (
                  <option key={cat} value={cat}>
                    📁 {cat}
                  </option>
                ))}
            </select>
          </div>

          {/* 4. Icône (Emoji) */}
          <div className="space-y-2 p-3 bg-amber-50/70 border border-amber-300/80 rounded-xl shadow-2xs">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-amber-950 uppercase tracking-wider flex items-center gap-1.5">
                <span>Icône (Emoji)</span>
              </label>
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  const autoIcon = getWordIcon({ kab: kabText, fr: frText, type: category } as WordItem) || getCategoryIcon(category) || '✨';
                  setIconText(autoIcon);
                }}
                className="text-[10px] font-bold text-amber-900 hover:text-amber-950 bg-amber-200/80 hover:bg-amber-300 px-2.5 py-1 rounded-lg border border-amber-400/80 cursor-pointer transition-all flex items-center gap-1.5 shadow-2xs active:scale-95"
                title="Déduire l'icône automatiquement selon le mot"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-700" />
                <span>Auto-détecter</span>
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="relative sm:w-28 shrink-0">
                <input
                  ref={iconInputRef}
                  type="text"
                  inputMode={useVirtualKeyboard ? "none" : "text"}
                  value={iconText}
                  placeholder="Ex: 🐕"
                  onFocus={() => setActiveInput('icon')}
                  onChange={(e) => setIconText(e.target.value)}
                  maxLength={5}
                  className={`w-full bg-white border-2 rounded-xl px-3 py-2 text-3xl text-center focus:outline-none transition-all shadow-2xs ${
                    activeInput === 'icon'
                      ? 'border-amber-600 shadow-[0_0_10px_rgba(217,119,6,0.2)]'
                      : 'border-[#ded4c3]'
                  }`}
                />
              </div>

              {/* Dynamic Suggestions & Quick Presets */}
              <div className="flex-1 flex flex-wrap items-center gap-1.5 bg-white/70 p-1.5 rounded-xl border border-amber-200/80 min-h-[44px]">
                {getWordIconSuggestions({
                  kab: kabText,
                  fr: frText,
                  variants: variantsText,
                  type: category
                }, 10).map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => {
                      playSound('step');
                      setIconText(emoji);
                    }}
                    className={`h-8 min-w-[32px] px-1.5 rounded-lg border text-base flex items-center justify-center transition-all cursor-pointer shadow-2xs ${
                      iconText === emoji
                        ? 'bg-amber-200 border-amber-500 scale-110 shadow-xs'
                        : 'bg-white hover:bg-amber-100/80 border-[#ded4c3] active:scale-95'
                    }`}
                    title={`Choisir ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Keyboard & Footer Container - Fixed at Bottom */}
      <div className="shrink-0 bg-[#ede4d4] border-t border-amber-900/15 shadow-[0_-8px_20px_rgba(120,53,15,0.08)] flex flex-col w-full">
        <div className="w-full max-w-xl mx-auto p-2 sm:p-3 flex flex-col gap-1 sm:gap-1.5">
          {/* ========================================================================= */}
          {/* VIRTUAL BERBER KEYBOARD (SCALE MATCHING CROSSWORD CHALLENGE)              */}
          {/* ========================================================================= */}
          <div className="flex flex-col gap-1 sm:gap-1.5">
            <div className="flex items-center justify-between pb-1 border-b border-amber-900/10 text-[10px] shrink-0 mb-0.5">
              <div className="flex items-center gap-1.5 text-amber-950 font-bold uppercase tracking-wider">
                <Keyboard className="w-3 h-3 text-amber-700" />
                <span>Clavier Berbère</span>
              </div>

              <div 
                className="flex items-center gap-2 cursor-pointer group" 
                onClick={() => setUseVirtualKeyboard(!useVirtualKeyboard)}
                title="Activer/Désactiver le clavier virtuel berbère"
              >
                <span className={`text-[9px] font-bold transition-colors uppercase tracking-wider ${useVirtualKeyboard ? 'text-amber-900 font-black' : 'text-stone-500'}`}>
                  {useVirtualKeyboard ? 'Virtuel' : 'Système'}
                </span>
                <button
                  type="button"
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-transparent transition-colors focus:outline-none ${
                    useVirtualKeyboard ? 'bg-amber-600' : 'bg-stone-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                      useVirtualKeyboard ? 'translate-x-2' : '-translate-x-2'
                    }`}
                  />
                </button>
              </div>
            </div>

            {useVirtualKeyboard && (
              <>
                {/* Row 1: A Z E R T Y U I O P */}
                <div className="flex justify-center gap-1 sm:gap-1.5 w-full">
                  {KEYBOARD_ROW_1.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onPointerDown={() => triggerKeyFeedback(k)}
                      onClick={() => handleVirtualKey(k)}
                      className={`flex-1 h-9.5 sm:h-11 bg-white hover:bg-amber-50 text-stone-900 font-mono font-bold text-xs sm:text-base rounded-lg border-2 border-[#ded4c3] hover:border-amber-400 shadow-2xs keyboard-key-illuminated flex items-center justify-center cursor-pointer min-w-0 ${
                        illuminatedKey === k ? 'is-pressed' : ''
                      }`}
                    >
                      {illuminatedKey === k && (
                        <span className="keyboard-key-callout">{isCapitalized ? k : k.toLowerCase()}</span>
                      )}
                      <span>{isCapitalized ? k : k.toLowerCase()}</span>
                    </button>
                  ))}
                </div>

                {/* Row 2: Q S D F G H J K L M */}
                <div className="flex justify-center gap-1 sm:gap-1.5 w-full">
                  {KEYBOARD_ROW_2.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onPointerDown={() => triggerKeyFeedback(k)}
                      onClick={() => handleVirtualKey(k)}
                      className={`flex-1 h-9.5 sm:h-11 bg-white hover:bg-amber-50 text-stone-900 font-mono font-bold text-xs sm:text-base rounded-lg border-2 border-[#ded4c3] hover:border-amber-400 shadow-2xs keyboard-key-illuminated flex items-center justify-center cursor-pointer min-w-0 ${
                        illuminatedKey === k ? 'is-pressed' : ''
                      }`}
                    >
                      {illuminatedKey === k && (
                        <span className="keyboard-key-callout">{isCapitalized ? k : k.toLowerCase()}</span>
                      )}
                      <span>{isCapitalized ? k : k.toLowerCase()}</span>
                    </button>
                  ))}
                </div>

                {/* Row 3: W X C V B N ' - */}
                <div className="flex justify-center gap-1 sm:gap-1.5 w-full">
                  {KEYBOARD_ROW_3.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onPointerDown={() => triggerKeyFeedback(k)}
                      onClick={() => handleVirtualKey(k)}
                      className={`flex-1 h-9.5 sm:h-11 bg-white hover:bg-amber-50 text-stone-900 font-mono font-bold text-xs sm:text-base rounded-lg border-2 border-[#ded4c3] hover:border-amber-400 shadow-2xs keyboard-key-illuminated flex items-center justify-center cursor-pointer min-w-0 ${
                        illuminatedKey === k ? 'is-pressed' : ''
                      }`}
                    >
                      {illuminatedKey === k && (
                        <span className="keyboard-key-callout">{isCapitalized ? k : k.toLowerCase()}</span>
                      )}
                      <span>{isCapitalized ? k : k.toLowerCase()}</span>
                    </button>
                  ))}
                </div>

                {/* Row 4: Dedicated Berber Ribbon (11 Amazigh letters) */}
                <div className="flex justify-center gap-0.5 sm:gap-1 w-full bg-amber-200/50 p-1 rounded-xl border border-amber-300">
                  {KEYBOARD_AMAZIGH_ROW.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onPointerDown={() => triggerKeyFeedback(k)}
                      onClick={() => handleVirtualKey(k)}
                      className={`flex-1 h-8.5 sm:h-10 bg-amber-100 hover:bg-amber-200 text-amber-950 font-mono font-black text-xs sm:text-base rounded-lg border-2 border-amber-400 keyboard-key-illuminated flex items-center justify-center cursor-pointer min-w-0 shadow-xs ${
                        illuminatedKey === k ? 'is-pressed' : ''
                      }`}
                    >
                      {illuminatedKey === k && (
                        <span className="keyboard-key-callout">{isCapitalized ? k : k.toLowerCase()}</span>
                      )}
                      <span>{isCapitalized ? k : k.toLowerCase()}</span>
                    </button>
                  ))}
                </div>

                {/* Row 5: Action Controls (MAJ + ESPACE + EFFACER) */}
                <div className="flex justify-center gap-1 sm:gap-1.5 w-full pt-0.5">
                  <button
                    type="button"
                    onPointerDown={() => triggerKeyFeedback('shift')}
                    onClick={() => setIsCapitalized(!isCapitalized)}
                    className={`flex-[1.5] h-9.5 sm:h-11 text-xs sm:text-sm font-bold rounded-lg border-2 keyboard-key-illuminated flex items-center justify-center gap-1 cursor-pointer shadow-2xs ${
                      isCapitalized
                        ? 'bg-amber-600 text-white border-amber-700 font-black'
                        : 'bg-amber-100 hover:bg-amber-200 text-amber-950 border-amber-300'
                    } ${illuminatedKey === 'shift' ? 'is-pressed' : ''}`}
                    title="Majuscules / Minuscules"
                  >
                    {illuminatedKey === 'shift' && (
                      <span className="keyboard-key-callout">⇧ MAJ</span>
                    )}
                    <span>⇧</span>
                    <span className="hidden sm:inline">MAJ</span>
                  </button>

                  <button
                    type="button"
                    onPointerDown={() => triggerKeyFeedback('space')}
                    onClick={handleSpace}
                    className={`flex-[4] h-9.5 sm:h-11 bg-white hover:bg-amber-50 text-stone-800 font-bold text-xs sm:text-sm rounded-lg border-2 border-[#ded4c3] keyboard-key-illuminated flex items-center justify-center gap-1 cursor-pointer shadow-2xs ${
                      illuminatedKey === 'space' ? 'is-pressed' : ''
                    }`}
                  >
                    {illuminatedKey === 'space' && (
                      <span className="keyboard-key-callout">␣ Espace</span>
                    )}
                    <span>␣ Espace</span>
                  </button>

                  <button
                    type="button"
                    onPointerDown={() => triggerKeyFeedback('backspace')}
                    onClick={handleBackspace}
                    className={`flex-[1.5] h-9.5 sm:h-11 bg-rose-50 hover:bg-rose-100 text-rose-900 font-bold text-xs sm:text-sm rounded-lg border-2 border-rose-300 keyboard-key-illuminated flex items-center justify-center gap-1 cursor-pointer shadow-2xs ${
                      illuminatedKey === 'backspace' ? 'is-pressed' : ''
                    }`}
                    title="Effacer"
                  >
                    {illuminatedKey === 'backspace' && (
                      <span className="keyboard-key-callout">⌫</span>
                    )}
                    <span>⌫</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bg-[#ede4d4] px-4 py-2.5 pb-[max(env(safe-area-inset-bottom),12px)] border-t border-amber-900/15 flex justify-center shrink-0 w-full">
          <div className="w-full max-w-xl flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                playSound('click');
                onClose();
              }}
              className="px-4 py-2 bg-white hover:bg-stone-100 text-stone-700 hover:text-stone-900 rounded-xl text-xs font-bold border border-[#ded4c3] shadow-2xs cursor-pointer transition-all"
            >
              Annuler
            </button>

            <button
              type="button"
              disabled={isSaving}
              onClick={handleSave}
              className="flex-1 max-w-xs px-5 py-2.5 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-black text-xs sm:text-sm uppercase tracking-wider rounded-xl shadow-md border border-amber-700 active:scale-95 transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              <span>{isSaving ? 'Enregistrement...' : 'Enregistrer'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
